/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useReducer, useState } from "react";
import { Else, If, Then, When } from "react-if";
import {
  Box,
  Button,
  FlexContainer,
  theme,
  Spacer,
  LoadMore,
} from "@nordcloud/gnui";
import type { Resource } from "~/generated/graphql";
import { DEFAULT_PAGE_SIZE, LOAD_MORE_INCREMENT } from "~/constants/pagination";
import { useComponentsSelector } from "~/hooks";
import { showSuccess } from "~/services/toast";
import { generateActionSuccessText, isNotEmpty } from "~/tools";
import { Item, SelectionMethod } from "~/types";
import { ResourceDragDrop } from "~/views/resourceGroups/components/Resource/components";
import { ResourceSidebar } from "~/views/resourceGroups/components/Resource/components/ResourceSidebar";
import { ResourceGroupExpression } from "~/views/resourceGroups/components/ResourceGroupExpression";
import { useUpdateResourceGroup } from "~/views/resourceGroups/hooks";
import {
  mapResourceToSelectedItem,
  mapResourceToItem,
} from "~/views/resourceGroups/utils";
import { useResourceGroup } from "../../ResourceGroupProvider";
import { ResourceSelector, ResourceWithSelectionMethod } from "../../types";
import { ResourcesTable } from "./components/ResourcesTable";

export type State = {
  isEditMode: boolean;
  selectedResources: string[];
  dynamicResources: Item[];
};

type ResourceData = NonNullable<ResourceSelector["resource"]>;

export function ResourcesTab() {
  const {
    resourceGroup: resourceGroupData,
    expression,
    setExpression,
  } = useResourceGroup();

  const [allResources, setAllResources] = useState<Resource[]>([]);
  const [editVisibleCount, setEditVisibleCount] =
    useState<number>(DEFAULT_PAGE_SIZE);

  const resourceGroup = {
    id: resourceGroupData?.id,
    resourceSelectors: resourceGroupData?.resourceSelectors?.filter(
      (resource) => !resource.tagsExpression
    ),
  };

  const expressionSelector = resourceGroupData?.resourceSelectors?.find(
    (resource) => resource.tagsExpression
  );

  const prepareResources =
    resourceGroup?.resourceSelectors?.map(
      ({ resource }) => resource?.id ?? ""
    ) ?? [];

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    {
      isEditMode: false,
      selectedResources: prepareResources,
      dynamicResources: [],
    }
  );

  const dynamicResources = expression
    ? resourceGroupData?.resourceSelectors?.find(
        (resource) => resource.dynamicResources
      )?.dynamicResources ?? []
    : [];

  const manualResources =
    resourceGroup?.resourceSelectors?.map(({ resource }) => resource) ?? [];

  const dynamicResourcesWithSelectionMethod: ResourceWithSelectionMethod[] =
    dynamicResources.map((resource) => ({
      ...resource,
      selectionMethod: SelectionMethod.dynamic,
    }));

  const manualResourcesWithSelectionMethod: ResourceWithSelectionMethod[] =
    manualResources
      .filter((resource): resource is ResourceData => !!resource)
      .map((resource) => ({
        ...resource,
        selectionMethod: SelectionMethod.manual,
      }));

  const resourceTableItems = [
    ...dynamicResourcesWithSelectionMethod,
    ...manualResourcesWithSelectionMethod,
  ];

  const [selectedOptions, setSelectedOptions] = useState([
    ...state.selectedResources,
  ]);

  const selectors = useMemo(() => {
    return allResources.length !== 0
      ? allResources
          .filter((resource) => selectedOptions.includes(resource.id))
          .map(mapResourceToSelectedItem)
      : resourceGroup?.resourceSelectors
          ?.map(({ resource }) => mapResourceToSelectedItem(resource))
          .filter((item) => selectedOptions.includes(item.id)) ?? [];
  }, [allResources, selectedOptions, resourceGroup?.resourceSelectors]);

  const handleSidebarSubmit = (ids: string[]) => {
    updateState({ selectedResources: ids });
    const manuallySelectedResources = allResources
      .map(mapResourceToSelectedItem)
      .filter((item) => ids.includes(item.id));

    const resources = [
      ...manuallySelectedResources,
      ...state.dynamicResources,
    ].filter((r) => isNotEmpty(r.id));

    handleShowSearch(resources);
  };

  const { items, handleDelete, handleShowSearch, handleDragEnd } =
    useComponentsSelector(selectors);

  const handleRemoveItem = (item: Item) => {
    updateState({
      selectedResources: state.selectedResources.filter((id) => id !== item.id),
    });

    handleDelete(item);
  };

  const [update, loading] = useUpdateResourceGroup({
    onSuccess: () => {
      updateState({
        isEditMode: false,
      });

      showSuccess(generateActionSuccessText("Resource Group")()("updated")());
    },
  });

  const handleEdit = () => {
    updateState({ isEditMode: true });
    setEditVisibleCount(DEFAULT_PAGE_SIZE);

    const dynamicItems = dynamicResources.map((resource) =>
      mapResourceToItem(resource, SelectionMethod.dynamic)
    );

    const newItems = dynamicItems.filter((item) => {
      const index = selectors.findIndex(
        (selectedItem) =>
          selectedItem.id === item.id && selectedItem.tag === item.tag
      );
      if (index === -1) {
        return true;
      }
      return false;
    });

    const resources = [...selectors, ...newItems].filter((r) =>
      isNotEmpty(r.id)
    );

    // Reset edit mode state
    handleShowSearch(resources);
  };

  const handleDynamicUpdate = (dynamicItems: Item[]) => {
    handleShowSearch([...selectors, ...dynamicItems]);
    updateState({ dynamicResources: dynamicItems });
  };

  const handleReorder = (ids: string[]) => {
    setSelectedOptions(ids);
  };

  const handleSave = (
    enteredExpression?: string,
    isAddResourcesManually = false
  ) => {
    const actualExpression = isAddResourcesManually
      ? expression
      : enteredExpression;

    const selectorIds = selectedOptions
      ?.map((item) => {
        const existed = resourceGroup?.resourceSelectors?.find(
          (i) => i.resource?.id === item
        );

        return {
          id: existed?.id ?? null,
          resourceId: existed?.resource?.id ?? item,
        };
      })
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.resourceId === item.resourceId)
      );

    const addOrder = selectorIds?.map((resourceId, i) => {
      return {
        resourceId: resourceId?.resourceId,
        order: i + 1,
        id: resourceId?.id ?? null,
      };
    });

    const prepareExpression = {
      order: 1,
      tagsExpression: actualExpression,
    };

    const resourceSelectors =
      isNotEmpty(actualExpression ?? "") &&
      actualExpression !== expressionSelector?.tagsExpression
        ? [...addOrder, prepareExpression]
        : addOrder;

    const selectorsToDelete =
      resourceGroup?.resourceSelectors
        ?.map((item) => ({
          id: item?.id ?? "",
          resourceId: item.resource?.id ?? "",
        }))
        ?.filter((item) => !selectedOptions.includes(item.resourceId))
        .map(({ id }) => id) ?? [];

    const expressionSelectorToDelete =
      expressionSelector?.tagsExpression !== actualExpression
        ? expressionSelector?.id
        : "";

    const resourceSelectorsToDelete = [
      ...selectorsToDelete,
      ...(expressionSelectorToDelete ? [expressionSelectorToDelete] : []),
    ];

    update({
      id: resourceGroup?.id ?? "",
      resourceSelectors: resourceSelectors,
      resourceSelectorsToDelete: resourceSelectorsToDelete,
    });
  };

  useEffect(() => {
    const ids = items.SELECTED_ITEMS.filter(
      (item) => item.tag === SelectionMethod.manual
    ).map((item) => item.id);

    setSelectedOptions(ids);
  }, [items.SELECTED_ITEMS, setSelectedOptions]);

  const handleCancelEdit = () => {
    setSelectedOptions(prepareResources);
    updateState({ isEditMode: false });
  };

  // Actual visible items count in edit view
  const currentCount = Math.min(editVisibleCount, items.SELECTED_ITEMS.length);

  return (
    <>
      <ResourceGroupExpression
        defaultValue={expression}
        handleDynamicUpdate={handleDynamicUpdate}
        handleSave={handleSave}
        setExpression={setExpression}
      />
      <Spacer height={theme.spacing.spacing03} />
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="flex-end">
          <If condition={state.isEditMode}>
            <Then>
              <Button
                ml={theme.spacing.spacing04}
                mr={theme.spacing.spacing04}
                onClick={() => handleSave(undefined, true)}
                disabled={loading}
              >
                Save
              </Button>
              <Button severity="low" icon="close" onClick={handleCancelEdit} />
            </Then>
            <Else>
              <Button ml="auto" onClick={handleEdit}>
                Edit Resources
              </Button>
            </Else>
          </If>
        </FlexContainer>
      </Box>

      <If condition={state.isEditMode}>
        <Then>
          <Box boxStyle="lightGrey" spacing="spacing04">
            <FlexContainer>
              <ResourceSidebar
                handleManualSubmit={handleSidebarSubmit}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                setAllResources={setAllResources}
              />
            </FlexContainer>
          </Box>
          <ResourceDragDrop
            {...{
              items,
              visibleCount: editVisibleCount,
              handleDragEnd,
              handleRemoveItem,
              handleReorder,
            }}
          />
          <When condition={items.SELECTED_ITEMS.length > LOAD_MORE_INCREMENT}>
            <LoadMore
              currentCount={currentCount}
              total={items.SELECTED_ITEMS.length}
              onLoadMore={() =>
                setEditVisibleCount((count) =>
                  Math.min(
                    count + LOAD_MORE_INCREMENT,
                    items.SELECTED_ITEMS.length
                  )
                )
              }
              onLoadLess={
                currentCount > DEFAULT_PAGE_SIZE
                  ? () => setEditVisibleCount(DEFAULT_PAGE_SIZE)
                  : undefined
              }
              isLoading={false}
            />
          </When>
        </Then>
        <Else>
          <ResourcesTable resources={resourceTableItems} />
        </Else>
      </If>
    </>
  );
}
